import * as Sentry from "@sentry/react";
import { z } from "zod";

/**
 * The envelopes every Genie backend answers in (genfive-backend-commons
 * `ResponseDto` / `ListResponseDto`), read leniently (DEDUPE-2 §3.3): a row
 * that does not have the shape is dropped and reported once, never thrown, so
 * one bad record cannot blank a page.
 */

export type ListResponse<TItem> = {
  data: TItem[];
  page: number;
  pageSize: number;
  totalCount: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const numberOr = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const report = (label: string, dropped: number, issues: z.ZodIssue[]) => {
  Sentry.captureException(
    new Error(`${label}: ${dropped} record(s) of an unexpected shape dropped`),
    { extra: { issues: issues.slice(0, 5) } }
  );
};

/** The rows of `data` that have the item's shape; the others are dropped and reported once. */
export const parseRows = <TSchema extends z.ZodTypeAny>(
  rows: unknown,
  item: TSchema,
  label: string
): z.output<TSchema>[] => {
  if (!Array.isArray(rows)) {
    if (rows !== undefined) report(label, 1, []);
    return [];
  }
  const parsed: z.output<TSchema>[] = [];
  const issues: z.ZodIssue[] = [];
  for (const row of rows) {
    const result = item.safeParse(row);
    if (result.success) {
      parsed.push(result.data);
    } else {
      issues.push(...result.error.issues);
    }
  }
  if (parsed.length !== rows.length) {
    report(label, rows.length - parsed.length, issues);
  }
  return parsed;
};

/** A list response: its valid rows, and the paging the backend sent (1, 1000, 0 when absent). */
export const parseListResponse = <TSchema extends z.ZodTypeAny>(
  response: unknown,
  item: TSchema,
  label: string
): ListResponse<z.output<TSchema>> => {
  const envelope = isRecord(response) ? response : {};
  return {
    data: parseRows(envelope["data"], item, label),
    page: numberOr(envelope["page"], 1),
    pageSize: numberOr(envelope["pageSize"], 1000),
    totalCount: numberOr(envelope["totalCount"], 0),
  };
};

/** A detail response that may hold no record: null when `data` is null or not of the shape. */
export const parseNullableResponse = <TSchema extends z.ZodTypeAny>(
  response: unknown,
  item: TSchema,
  label: string
): { data: z.output<TSchema> | null } => {
  const data = isRecord(response) ? response["data"] : undefined;
  if (data === null || data === undefined) return { data: null };
  const [record] = parseRows([data], item, label);
  return { data: record ?? null };
};

/**
 * A write's answer, `{ data }` with the one record it made: strict, since the
 * caller goes on to use it (a record of another shape fails the request).
 */
export const parseDetailResponse = <TSchema extends z.ZodTypeAny>(
  response: unknown,
  item: TSchema
): { data: z.output<TSchema> } => ({
  data: item.parse(isRecord(response) ? response["data"] : undefined),
});
